import { createHashRouter, Navigate, useLocation, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { RequirePermission } from '@/components/RequirePermission';
import { NotBuiltYet } from '@/components/states';
import { HomePage } from '@/workspaces/home/HomePage';
import { SchoolSalesPage } from '@/workspaces/crm/SchoolSalesPage';
import { TransakcjeParytetPage } from '@/workspaces/crm/TransakcjeParytetPage';
import { OpiniePage } from '@/workspaces/crm/OpiniePage';
import { UzytkownicyPage } from '@/workspaces/admin/UzytkownicyPage';
import { SzkolyIRestauracjePage } from '@/workspaces/admin/SzkolyIRestauracjePage';
import { ModeracjaMenuPage } from '@/workspaces/admin/ModeracjaMenuPage';
import { DzisiejszeZamowieniaPage } from '@/workspaces/restaurant/DzisiejszeZamowieniaPage';
import { SzkolyRestauracjiPage } from '@/workspaces/restaurant/SzkolyRestauracjiPage';
import { TransakcjeRestauracjiPage } from '@/workspaces/restaurant/TransakcjeRestauracjiPage';
import { RaportyDostawPage } from '@/workspaces/restaurant/RaportyDostawPage';
import { MenuRestauracjiPage } from '@/workspaces/restaurant/MenuRestauracjiPage';
import { PowlokaRestauracji } from '@/restauracja/PowlokaRestauracji';
import { DaniaPage } from '@/workspaces/restaurant/DaniaPage';
import { DishCatalogPage } from '@/workspaces/restaurants/DishCatalogPage';
import { RestaurantSalesPage } from '@/workspaces/restaurants/RestaurantSalesPage';
import { RozliczeniaPage } from '@/workspaces/restaurants/RozliczeniaPage';
import { GrafikRestauracjiPage } from '@/workspaces/restaurants/GrafikRestauracjiPage';
import { DishSalesPage } from '@/workspaces/restaurants/DishSalesPage';
import { RestaurantSchoolsPage } from '@/workspaces/restaurants/RestaurantSchoolsPage';
import { DriveOverviewPage } from '@/workspaces/drive/DriveOverviewPage';
import { DriveSchoolsPage } from '@/workspaces/drive/DriveSchoolsPage';
import { DriveHistoryPage } from '@/workspaces/drive/DriveHistoryPage';
import { InsightsPage } from '@/workspaces/insights/InsightsPage';
import { PowiazaniaPage } from '@/workspaces/insights/PowiazaniaPage';
import { SystemStatusPage } from '@/workspaces/system/SystemStatusPage';
import { GotowoscPage } from '@/workspaces/system/GotowoscPage';
import { JakoscDanychPage } from '@/workspaces/system/JakoscDanychPage';
import { SkorkiPage } from '@/workspaces/tresci/SkorkiPage';
import { PasekPage } from '@/workspaces/tresci/PasekPage';
import { WORKSPACES, findWorkspace, sectionPath, type WorkspaceSection } from './workspaces';

// Routing hash-owy (#/crm) — świadomy wybór: pozwala hostować Control Center jako statyczne
// pliki pod dowolnym prefiksem, bez konfigurowania przepisywania ścieżek na serwerze.
// Dokładnie ten sam powód, dla którego panel admina Drive używa hash-routingu.

function sectionOf(workspaceId: string, sectionId: string): WorkspaceSection {
  const ws = findWorkspace(workspaceId);
  const section = ws?.sections.find((s) => s.id === sectionId);
  if (!ws || !section) throw new Error(`Nieznana sekcja ${workspaceId}/${sectionId}`);
  return section;
}

/** Ekran zastępczy budowany Z METADANYCH nawigacji — opis blokera nie rozjeżdża się z menu. */
function Placeholder({ workspaceId, sectionId }: { workspaceId: string; sectionId: string }) {
  const section = sectionOf(workspaceId, sectionId);
  // `live` i `local` mają prawdziwy komponent — ekran zastępczy dla nich to pomyłka w trasach.
  if (section.maturity === 'live' || section.maturity === 'local') {
    throw new Error(`Sekcja ${sectionId} ma dojrzałość ${section.maturity} — brak komponentu.`);
  }
  return <NotBuiltYet title={section.label} maturity={section.maturity} note={section.note} />;
}

function guarded(workspaceId: string, sectionId: string, element: React.ReactNode) {
  const section = sectionOf(workspaceId, sectionId);
  return <RequirePermission permissions={section.permissions}>{element}</RequirePermission>;
}

/**
 * Przekierowanie starego adresu na nowy Z ZACHOWANIEM parametrów i kotwicy. Zwykłe
 * `<Navigate to="/x" />` gubi `?widok=miesieczne&mrok=2026&miesiac=9`, a z nim zakładkę, którą
 * ktoś zapisał w ulubionych — dostałby inny ekran niż ten, który zapamiętał.
 */
function Przekieruj({ na }: { na: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: na, search, hash }} replace />;
}

/**
 * Stare adresy → nowe (OWNER 2026-10-10: Rozliczenia i Grafik przeniesione do Administracji,
 * Administracja pod `/administracja`). To NIE są sekcje nawigacji — nie trafiają do menu ani do palety
 * poleceń; istnieją wyłącznie po to, żeby stare zakładki i linki nie kończyły się pustym ekranem.
 * Dawna „Organizacja” (relacja restauracja ↔ szkoła) prowadzi do „Szkoły i restauracje”.
 */
export const ALIASY: readonly { z: string; na: string }[] = [
  { z: 'admin', na: '/administracja' },
  { z: 'admin/schools', na: '/administracja/szkoly-i-restauracje' },
  { z: 'admin/organizacja', na: '/administracja/szkoly-i-restauracje' },
  { z: 'admin/grafik', na: '/administracja/grafik' },
  { z: 'admin/menu', na: '/administracja/moderacja-menu' },
  { z: 'restauracje/rozliczenia', na: '/administracja/rozliczenia' },
  { z: 'restauracje/grafik', na: '/administracja/grafik' },
];

export const routes: RouteObject[] = [
  /*
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  PANEL RESTAURACJI — ODDZIELNA GAŁĄŹ, POZA POWŁOKĄ CONTROL CENTER
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *
   *  ┌─ DLACZEGO STOI PRZED `/` I POZA `<AppShell />` ────────────────────────────────────────┐
   *  │ Gdyby był dzieckiem `<AppShell />`, dziedziczyłby jego pas górny, szynę nawigacji,     │
   *  │ przełącznik paneli i podgląd restauracji — i wtedy „ukrycie" tych elementów dla        │
   *  │ najemcy byłoby serią warunków rozsypanych po powłoce konsoli. Pierwszy pominięty       │
   *  │ warunek pokazałby restauracji Administrację.                                            │
   *  │                                                                                         │
   *  │ Osobna gałąź znaczy, że tych elementów NIE MA CZEGO ukrywać: nie są renderowane.        │
   *  └─────────────────────────────────────────────────────────────────────────────────────────┘
   *
   *  DWA RÓŻNE ADRESY, DWA RÓŻNE KONTEKSTY AUTORYZACJI — i tak ma zostać:
   *    `#/restauracja`        REAL_RESTAURANT_SESSION  — zakres z tożsamości najemcy,
   *    `#/panel-restauracji`  OWNER_RESTAURANT_PREVIEW — zakres z sesji podglądu właściciela.
   *
   *  Komponenty ekranów są WSPÓLNE i to jest zamierzone: dwie kopie tego samego widoku
   *  rozjechałyby się przy pierwszej poprawce, a właściciel oglądałby coś innego niż najemca.
   */
  {
    path: '/restauracja',
    element: <PowlokaRestauracji />,
    children: [
      { index: true, element: <DzisiejszeZamowieniaPage /> },
      { path: 'dania', element: <DaniaPage /> },
      { path: 'menu', element: <MenuRestauracjiPage /> },
      { path: 'szkoly', element: <SzkolyRestauracjiPage /> },
      { path: 'transakcje', element: <TransakcjeRestauracjiPage /> },
      { path: 'raporty-dostaw', element: <RaportyDostawPage /> },
      /*
       * Nieznana ścieżka WEWNĄTRZ panelu wraca na ekran domyślny panelu, a nie na `/`.
       * Wyrzucenie najemcy do konsoli byłoby wysłaniem go tam, gdzie i tak nic nie zobaczy.
       */
      { path: '*', element: <Navigate to="/restauracja" replace /> },
    ],
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: guarded('home', 'overview', <HomePage />) },

      { path: 'administracja', element: guarded('admin', 'users', <UzytkownicyPage />) },
      {
        path: 'administracja/szkoly-i-restauracje',
        element: guarded('admin', 'schools', <SzkolyIRestauracjePage />),
      },
      { path: 'administracja/moderacja-menu', element: guarded('admin', 'menu-moderacja', <ModeracjaMenuPage />) },
      // Rozliczenia i Grafik: TE SAME komponenty co wcześniej w Restauracjach — przeniesiony jest adres.
      { path: 'administracja/rozliczenia', element: guarded('admin', 'settlements', <RozliczeniaPage />) },
      { path: 'administracja/grafik', element: guarded('admin', 'schedule', <GrafikRestauracjiPage />) },
      ...ALIASY.map(({ z, na }) => ({ path: z, element: <Przekieruj na={na} /> })),

      { path: 'restauracje', element: guarded('restaurants', 'sales', <RestaurantSalesPage />) },
      { path: 'restauracje/dania', element: guarded('restaurants', 'dishes', <DishSalesPage />) },
      { path: 'restauracje/szkoly', element: guarded('restaurants', 'schools', <RestaurantSchoolsPage />) },
      { path: 'restauracje/katalog', element: guarded('restaurants', 'catalog', <DishCatalogPage />) },
      {
        path: 'restauracje/menu',
        element: guarded('restaurants', 'menu', <Placeholder workspaceId="restaurants" sectionId="menu" />),
      },

      { path: 'panel-restauracji', element: guarded('restaurant-panel', 'dzisiaj', <DzisiejszeZamowieniaPage />) },
      { path: 'panel-restauracji/dania', element: guarded('restaurant-panel', 'dania', <DaniaPage />) },
      { path: 'panel-restauracji/szkoly', element: guarded('restaurant-panel', 'szkoly', <SzkolyRestauracjiPage />) },
      {
        path: 'panel-restauracji/transakcje',
        element: guarded('restaurant-panel', 'transakcje', <TransakcjeRestauracjiPage />),
      },
      {
        // Ten sam komponent i ta sama usługa danych, co w prawdziwej sesji restauracji.
        // Różni się WYŁĄCZNIE tym, skąd serwer bierze zakres — a nie tym, co liczy.
        path: 'panel-restauracji/raporty-dostaw',
        element: guarded('restaurant-panel', 'raporty-dostaw', <RaportyDostawPage />),
      },

      { path: 'crm', element: guarded('crm', 'school-sales', <SchoolSalesPage />) },
      { path: 'crm/transakcje', element: guarded('crm', 'transactions', <TransakcjeParytetPage />) },
      { path: 'crm/opinie', element: guarded('crm', 'opinie', <OpiniePage />) },
      {
        path: 'crm/kampanie',
        element: guarded('crm', 'campaigns', <Placeholder workspaceId="crm" sectionId="campaigns" />),
      },

      // Przestrzeń Dostaw NIE jest bramkowana uprawnieniem: sama w sobie prosi o podłączenie
      // systemu kierowców i bez tej sesji nie pokazuje żadnych danych. Bramka jest po stronie
      // backendu Drive (requireAuth + requirePermission).
      { path: 'drive', element: <DriveOverviewPage /> },
      { path: 'drive/szkoly', element: <DriveSchoolsPage /> },
      { path: 'drive/historia', element: <DriveHistoryPage /> },

      { path: 'insights', element: guarded('insights', 'all', <InsightsPage />) },
      { path: 'insights/powiazania', element: guarded('insights', 'coincidence', <PowiazaniaPage />) },

      { path: 'tresci', element: guarded('tresci', 'skorki', <SkorkiPage />) },
      { path: 'tresci/pasek', element: guarded('tresci', 'pasek', <PasekPage />) },

      { path: 'raporty', element: guarded('reports', 'reports', <Placeholder workspaceId="reports" sectionId="reports" />) },

      // Status połączeń celowo BEZ bramki sesji: to jedyny ekran diagnostyczny, a nie pokazuje
      // żadnych danych domenowych — tylko konfigurację i to, czy backend odpowiada.
      { path: 'system', element: <SystemStatusPage /> },
      /* Gotowość i Jakość danych celowo BEZ strażnika uprawnień — tak samo jak status
         połączeń. Te ekrany mówią o STANIE NARZĘDZIA, a nie o danych biznesowych; osoba,
         która nie widzi sprzedaży, nadal musi móc sprawdzić, czemu jej nie widzi. */
      { path: 'system/gotowosc', element: <GotowoscPage /> },
      { path: 'system/jakosc', element: <JakoscDanychPage /> },
      { path: 'system/audyt', element: guarded('system', 'audit', <Placeholder workspaceId="system" sectionId="audit" />) },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createHashRouter(routes);

/** Wszystkie ścieżki nawigacji — używane przez test spójności menu i routingu. */
export function navigationPaths(): string[] {
  return WORKSPACES.flatMap((w) => w.sections.map((s) => sectionPath(w, s)));
}
